import { useState, type ReactNode } from "react";
import dayjs from "dayjs";
import clsx from "clsx";
import { db } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { navigate } from "../../lib/router";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Loading, Panel, Seg, Sheet, fmtShort, useToast } from "../../components/ui";
import { openPerson } from "../../components/PersonDrawer";
import { Columns, HBars } from "../../components/charts";
import { MONTHS } from "../leads/Dashboards";

const pct = (n?: number | null) => (n == null ? "—" : `${Math.round(Number(n) * 100)}%`);
type Tone = "navy" | "gold" | "green" | "amber" | "red" | "grey" | "sky" | "hot" | "warm" | "cool" | "dormant";

/** Excel-style tile: big number, caps label, small explainer. Tone colours the top rule. */
function Tile({ value, label, sub, tone = "gold", onClick, alert }: { value: ReactNode; label: string; sub?: ReactNode; tone?: Tone; onClick?: () => void; alert?: boolean }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className={clsx("tile", `t-${tone}`, alert && "alert", onClick && "clickable")} onClick={onClick}>
      <span className="tv">{value}</span>
      <span className="tl">{label}</span>
      {sub && <span className="ts">{sub}</span>}
    </Tag>
  );
}

function Section({ title, sub, children, actions }: { title: string; sub?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="dsec">
      <header><h2>{title}{sub && <span> · {sub}</span>}</h2>{actions}</header>
      {children}
    </section>
  );
}

export default function TeamDashboard() {
  const { can, perms } = useSession();
  const [year, setYear] = useState(dayjs().year());
  const [view, setView] = useState<"months" | "quarters">("months");
  const [loadExport, setLoadExport] = useState(false);
  const [more, setMore] = useState<{ overdue: boolean; longest: boolean }>({ overdue: false, longest: false });
  const q = useQuery(`dash:team:${year}`, () => db.rpc<any>("dash_team", { p_year: year }));
  const ext = useQuery("team:extended", () => db.select<{ member_id: string }>("team_overview", { select: "member_id", membership_extended: "is.true", status: "eq.Active", licensed: "is.false" })
    .then((r) => new Set(r.map((x) => x.member_id))));
  const d = q.data;
  const years: number[] = [...new Set([dayjs().year(), ...((d?.years ?? []) as number[])])].sort((a, b) => b - a);
  const ms: any[] = (d?.milestones ?? []);
  const ticks = ms.filter((m) => !m.is_count);
  const months: any[] = d?.by_month ?? [];
  const sumRows = (rows: any[]) => ({
    joined: rows.reduce((s, r) => s + r.joined, 0), on_tracker: rows.reduce((s, r) => s + r.on_tracker, 0),
    done: Object.fromEntries(ms.map((m) => [m.key, rows.reduce((s, r) => s + Number(r.done?.[m.key] ?? 0), 0)])),
  });
  const quarters = [1, 2, 3, 4].map((qq) => ({ label: `Q${qq}`, key: qq, ...sumRows(months.slice((qq - 1) * 3, qq * 3)) }));
  const total = sumRows(months);
  const now = dayjs();
  const isNow = (kind: "m" | "q", k: number) => year === now.year() && (kind === "m" ? now.month() + 1 === k : Math.ceil((now.month() + 1) / 3) === k);
  const rows = view === "months" ? months.map((m) => ({ ...m, label: `${MONTHS[m.month - 1]} ${year}`, key: m.month })) : quarters;
  const h = d?.health ?? {}, r = d?.recruiting ?? {}, t = d?.touchpoints ?? {};
  const overdue: any[] = d?.overdue_list ?? [], longest: any[] = d?.longest_list ?? [];
  const canExport = can("team", "edit") && perms.team?.scope === "all";   // the company export is for whoever manages the whole team

  return (
    <>
      <PageHead title="Team dashboard" sub="Recruiting, onboarding and check-ins. Live from the team, the recruit tracker and the contact log."
        actions={<>
          <span className="lbl small muted">Year</span>
          <select className="select" style={{ width: "auto", borderRadius: 999 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>{years.map((y) => <option key={y}>{y}</option>)}</select>
          {canExport && <button className="btn" onClick={() => setLoadExport(true)}>Update from export</button>}
        </>} />
      <ErrorBox error={q.error} />
      {!d ? <Loading rows={10} /> : (
        <div className="stack">
          <NeedsYou t={t} h={h} r={r} />

          <Section title="Team health" sub="right now">
            <div className="tiles">
              <Tile tone="navy" value={h.active} label="Active team members" sub="Status = Active" onClick={() => navigate("/team/roster?status=Active")} />
              <Tile value={h.licensed} label="Licensed (active)" sub={`${h.active ? Math.round((h.licensed / h.active) * 100) : 0}% of active team`} onClick={() => navigate("/team/roster?status=Active&licensed=yes")} />
              <Tile tone="grey" value={h.terminated} label="Terminated" sub="No longer active" onClick={() => navigate("/team/roster?status=Terminated")} />
              <Tile tone={h.not_in_export ? "amber" : "green"} alert={h.not_in_export > 0} value={h.export_loaded ? h.not_in_export : "—"} label="On my team, not in export"
                sub={h.export_loaded ? `Export loaded ${fmtShort(h.export_at)}` : "Load the platform export"} onClick={canExport ? () => setLoadExport(true) : undefined} />
              <Tile tone={h.missing_from_team ? "amber" : "green"} alert={h.missing_from_team > 0} value={h.export_loaded ? h.missing_from_team : "—"} label="In export, not on my team"
                sub="Add them to the team" onClick={canExport ? () => setLoadExport(true) : undefined} />
              <Tile tone={h.deadline_soon ? "red" : "green"} alert={h.deadline_soon > 0} value={h.deadline_soon} label="Membership expiring in 90 days" sub="Active, not licensed · extended dates count"
                onClick={() => document.getElementById("licensing-watch")?.scrollIntoView({ behavior: "smooth" })} />
              <Tile tone="green" value={ext.data?.size ?? 0} label="Memberships extended" sub="Confirmed extensions, not yet licensed" onClick={() => navigate("/team/roster?status=Active&filter=extended")} />
            </div>
          </Section>

          <Section title="Assistant touchpoints" sub="who has been contacted, and who is slipping">
            <div className="tiles">
              <Tile tone="navy" value={t.this_week} label="Contacts this week" sub="Contact log since Monday" />
              <Tile tone="green" value={t.green} label="Green" sub="Within 7 / 14 days" />
              <Tile tone="amber" value={t.amber} label="Amber" sub="Due for a check-in" />
              <Tile tone="red" alert={t.red > 0} value={t.red} label="Red" sub="Overdue for contact" onClick={() => navigate("/team/roster?status=Active&filter=nocontact")} />
              <Tile tone="grey" alert={t.not_contacted > 0} value={t.not_contacted} label="Not contacted yet" sub="Active, no log entry" onClick={() => navigate("/team/roster?status=Active&filter=nocontact")} />
              <Tile tone="red" alert={t.overdue_commitments > 0} value={t.overdue_commitments} label="Overdue commitments" sub="Their own deadline passed" />
              <Tile tone="red" alert={t.overdue_milestones > 0} value={t.overdue_milestones ?? 0} label="Tracker steps overdue" sub={`${t.members_behind ?? 0} recruits behind`} onClick={() => document.getElementById("behind-list")?.scrollIntoView({ behavior: "smooth" })} />
            </div>
            <div className="tiles">
              <Tile tone="navy" value={t.open_commitments} label="Open commitments" sub="Pending in contact log" />
              <Tile tone="hot" value={t.hot} label="Hot spark" sub="Acting on their own" />
              <Tile tone="warm" value={t.warm} label="Warm spark" sub="Delivers when nudged" />
              <Tile tone="cool" value={t.cool} label="Cool spark" sub="Keeps delaying" />
              <Tile tone="dormant" value={t.dormant} label="Dormant spark" sub="Gone quiet" />
              <Tile tone={(t.coverage ?? 0) >= 0.8 ? "green" : (t.coverage ?? 0) >= 0.5 ? "amber" : "red"} value={pct(t.coverage)} label="Contact coverage" sub="Active agents green or amber" />
            </div>
          </Section>

          <div className="grid-2">
            <Panel title="Follow up now" hint="Overdue commitments, oldest first">
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th>Agent</th><th>Topic</th><th>Commitment</th><th>Deadline</th><th className="num">Days late</th></tr></thead>
                  <tbody>
                    {overdue.length === 0 && <tr><td colSpan={5}><Empty title="Nothing overdue">Every commitment is on time.</Empty></td></tr>}
                    {(more.overdue ? overdue : overdue.slice(0, 8)).map((x, i) => (
                      <tr key={i} data-click onClick={() => openPerson(x.person_id)}>
                        <td><b style={{ color: "var(--navy)" }}>{x.name}</b></td><td>{x.topic}</td><td>{x.commitment}</td>
                        <td className="nowrap">{fmtShort(x.due)}</td><td className="num"><span className="pill alert">{x.days_late}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {overdue.length > 8 && <button className="btn ghost small more" onClick={() => setMore({ ...more, overdue: !more.overdue })}>{more.overdue ? "Show fewer" : `+ ${overdue.length - 8} more`}</button>}
            </Panel>
            <Panel title="Longest without contact" hint="Red or never contacted">
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th>Agent</th><th>Recruiter</th><th>Licensed</th><th>Last contact</th><th className="num">Days</th><th>Spark</th></tr></thead>
                  <tbody>
                    {longest.length === 0 && <tr><td colSpan={6}><Empty title="Everyone is in touch" /></td></tr>}
                    {(more.longest ? longest : longest.slice(0, 8)).map((x) => (
                      <tr key={x.member_id} data-click onClick={() => navigate(`/team/members/${x.member_id}`)}>
                        <td><b style={{ color: "var(--navy)" }}>{x.name}</b></td><td>{x.recruiter}</td><td>{x.licensed ? "Yes" : "No"}</td>
                        <td className="nowrap">{x.last_contact ? fmtShort(x.last_contact) : <span className="muted">never</span>}</td>
                        <td className="num">{x.days ?? <span className="muted">never</span>}</td><td>{x.spark ? <span className={`spark s-${x.spark}`}>{x.spark}</span> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {longest.length > 8 && <button className="btn ghost small more" onClick={() => setMore({ ...more, longest: !more.longest })}>{more.longest ? "Show fewer" : `+ ${longest.length - 8} more`}</button>}
            </Panel>
          </div>

          <Section title="Recruiting" sub={String(year)}>
            <div className="tiles">
              <Tile tone="navy" value={r.new_recruits} label="New recruits" sub="By recruit date (incl. terminated)" />
              <Tile tone={(r.still_active ?? 1) >= 0.8 ? "green" : "amber"} value={pct(r.still_active)} label="Still active" sub="Retention of that year's recruits" />
              <Tile value={r.on_tracker} label="On recruit tracker" sub="Recruits tracked that year" onClick={() => navigate("/team/tracker")} />
              <Tile tone="sky" value={r.this_quarter} label={`This quarter (Q${Math.ceil((now.month() + 1) / 3)})`} sub="Tracked recruits, current quarter" />
              <Tile tone="green" value={r.licensed_from_tracker} label="Licensed from tracker" sub="Licensed ✓ on that year's tracker" />
              <Tile tone={r.not_on_tracker ? "red" : "green"} alert={r.not_on_tracker > 0} value={r.not_on_tracker} label="Joined, not on tracker" sub="Add them to the tracker" onClick={() => navigate("/team/tracker")} />
            </div>
          </Section>

          <Panel title={view === "months" ? `Month by month · ${year}` : `Quarter by quarter · ${year}`} hint="How many of each period's recruits completed each step. Current period highlighted."
            actions={<Seg value={view} onChange={setView} options={[{ value: "months", label: "Months" }, { value: "quarters", label: "Quarters" }]} />}>
            <div className="table-wrap">
              <table className="data mtable">
                <thead><tr><th>{view === "months" ? "Month" : "Quarter"}</th><th className="num">Joined</th><th className="num">On tracker</th>{ms.map((m) => <th key={m.key} className="num">{m.label}</th>)}</tr></thead>
                <tbody>
                  {rows.map((x: any) => (
                    <tr key={x.label} className={clsx(isNow(view === "months" ? "m" : "q", x.key) && "now")}>
                      <td className="nowrap"><b style={{ color: "var(--navy)" }}>{x.label}</b></td>
                      <td className="num">{x.joined}</td><td className="num">{x.on_tracker}</td>
                      {ms.map((m) => {
                        const v = Number(x.done?.[m.key] ?? 0);
                        return <td key={m.key} className={clsx("num", v === 0 && "zero", !m.is_count && x.on_tracker > 0 && v >= x.on_tracker && "full")}>{v}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td>Year total</td><td className="num">{total.joined}</td><td className="num">{total.on_tracker}</td>{ms.map((m) => <td key={m.key} className="num">{total.done[m.key]}</td>)}</tr>
                  <tr className="pctrow"><td>% of tracked</td><td /><td />{ms.map((m) => <td key={m.key} className="num">{total.on_tracker ? pct(total.done[m.key] / total.on_tracker) : "—"}</td>)}</tr>
                </tfoot>
              </table>
            </div>
          </Panel>

          <div className="grid-2">
            <Panel title="Joined vs. on tracker" hint={`By month · ${year}`}>
              <div className="panel-body">
                <Columns labels={MONTHS} tipTitle={(i) => `${MONTHS[i]} ${year}`}
                  series={[{ key: "j", label: "Joined", color: "#0094c8", values: months.map((m) => m.joined) }, { key: "o", label: "On tracker", color: "#132d9c", values: months.map((m) => m.on_tracker) }]} />
              </div>
            </Panel>
            <Panel title="Milestone completion" hint={`% of ${total.on_tracker} tracked recruits · ${year}`}>
              <div className="panel-body">
                <HBars rows={ticks.map((m) => ({ label: m.label, value: total.on_tracker ? Math.round((total.done[m.key] / total.on_tracker) * 100) : 0 }))} format={(n) => `${n}%`} color="var(--royal)" />
              </div>
            </Panel>
          </div>

          {(d.behind_list ?? []).length > 0 && (
            <Panel title="Behind on the tracker" hint="Recruits with steps past their due date, most behind first" className="mt16">
              <div className="table-wrap" id="behind-list">
                <table className="data">
                  <thead><tr><th>Recruit</th><th>Recruiter</th><th>Joined</th><th className="num">Steps overdue</th><th>Progress</th></tr></thead>
                  <tbody>
                    {(d.behind_list as any[]).map((x) => (
                      <tr key={x.member_id} data-click onClick={() => navigate(`/team/members/${x.member_id}`)}>
                        <td><b style={{ color: "var(--navy)" }}>{x.name}</b></td><td>{x.recruiter}</td>
                        <td className="nowrap small">{fmtShort(x.recruit_date)}</td>
                        <td className="num"><span className="pill alert">{x.late_steps}</span></td>
                        <td className="small">{Math.round((x.progress ?? 0) * 100)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          <div className="grid-2">
            <Panel title="Licensing watch" hint="Membership expiring within 90 days (extended dates count)">
              <div className="table-wrap" id="licensing-watch">
                <table className="data">
                  <thead><tr><th>Agent</th><th>Recruiter</th><th>Membership expires</th><th className="num">Days left</th></tr></thead>
                  <tbody>
                    {(d.licensing_watch ?? []).length === 0 && <tr><td colSpan={4}><Empty title="No one close to the deadline" /></td></tr>}
                    {(d.licensing_watch ?? []).map((x: any) => (
                      <tr key={x.member_id} data-click onClick={() => navigate(`/team/members/${x.member_id}`)}>
                        <td><b style={{ color: "var(--navy)" }}>{x.name}</b><div className="small muted">{x.agent_code}</div></td><td>{x.recruiter}</td>
                        <td className="nowrap">{fmtShort(x.deadline)}{ext.data?.has(x.member_id) && <span className="pill warm" style={{ marginLeft: 4 }}>Extended</span>}</td>
                        <td className="num"><span className={clsx("pill", x.days_left <= 30 ? "alert" : x.days_left <= 60 ? "cooling" : "")}>{x.days_left === 0 ? "today" : x.days_left}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
            <Panel title="Recruiter board" hint={`Recruits in ${year} · still active · active direct team`}>
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th>Recruiter</th><th style={{ minWidth: 90 }}>Recruits</th><th className="num">Still active</th><th className="num">Active team</th></tr></thead>
                  <tbody>
                    {(() => { const mx = Math.max(1, ...(d.recruiter_board ?? []).map((x: any) => x.recruits)); return (d.recruiter_board ?? []).map((x: any) => (
                      <tr key={x.member_id} data-click onClick={() => navigate(`/team/members/${x.member_id}`)}>
                        <td>{x.name}<div className="small muted">{x.code}</div></td>
                        <td><div className="dbar"><i style={{ width: `${(x.recruits / mx) * 100}%` }} /><span>{x.recruits}</span></div></td>
                        <td className="num">{x.still_active}</td><td className="num">{x.active_team}</td>
                      </tr>)); })()}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
          <div className="grid-2">
            <Panel title="Active team by province">
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th>Province / State</th><th style={{ minWidth: 90 }}>Active</th><th className="num">Licensed</th></tr></thead>
                  <tbody>
                    {(() => { const mx = Math.max(1, ...(d.by_province ?? []).map((x: any) => x.active)); return (d.by_province ?? []).map((x: any) => (
                      <tr key={x.province}><td>{x.province}</td><td><div className="dbar sky"><i style={{ width: `${(x.active / mx) * 100}%` }} /><span>{x.active}</span></div></td><td className="num">{x.licensed}</td></tr>)); })()}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      )}
      {loadExport && <ExportLoader onClose={() => setLoadExport(false)} />}
    </>
  );
}

/** The accountability strip: the few things that need you today, in plain words. */
function NeedsYou({ t, h, r }: { t: any; h: any; r: any }) {
  const items: { tone: "red" | "amber" | "green"; text: ReactNode; to?: () => void }[] = [];
  if (t.overdue_commitments) items.push({ tone: "red", text: <><b>{t.overdue_commitments}</b> commitment{t.overdue_commitments > 1 ? "s are" : " is"} past the agent's own deadline</> });
  if (t.overdue_milestones) items.push({ tone: "red", text: <><b>{t.members_behind}</b> recruit{t.members_behind > 1 ? "s are" : " is"} behind on the tracker ({t.overdue_milestones} steps overdue)</>, to: () => document.getElementById("behind-list")?.scrollIntoView({ behavior: "smooth" }) });
  if (t.not_contacted + t.red) items.push({ tone: "red", text: <><b>{t.not_contacted + t.red}</b> active people are red or never contacted</>, to: () => navigate("/team/roster?status=Active&filter=nocontact") });
  if (h.deadline_soon) items.push({ tone: "amber", text: <><b>{h.deadline_soon}</b> with membership expiring soon</>, to: () => document.getElementById("licensing-watch")?.scrollIntoView({ behavior: "smooth" }) });
  if (r.not_on_tracker) items.push({ tone: "amber", text: <><b>{r.not_on_tracker}</b> recruits joined but aren't on the tracker</>, to: () => navigate("/team/tracker") });
  const cov = Math.round((t.coverage ?? 0) * 100);
  return (
    <div className="needs">
      <div className="needs-score">
        <span className="ns-v">{cov}%</span>
        <span className="ns-l">contact coverage</span>
        <span className="ns-bar"><i style={{ width: `${cov}%` }} /></span>
        <span className="ns-s">Goal: 80%+ of active agents green or amber</span>
      </div>
      <div className="needs-list">
        <h3>{items.length ? "Needs you now" : "All clear"}</h3>
        {items.length === 0 && <p className="muted">No overdue commitments, everyone in touch, no deadline at risk. Keep the rhythm.</p>}
        {items.map((it, i) => (
          <button key={i} className={clsx("need", it.tone)} onClick={it.to} disabled={!it.to}>
            <i />{it.text}{it.to && <span className="go">→</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Paste the platform export (Active Agent + Licensed Agent lists) straight from Excel or the website. */
function ExportLoader({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [active, setActive] = useState("");
  const [licensed, setLicensed] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const [result, setResult] = useState<any>(null);

  const parseActive = (txt: string) => {
    const lines = txt.split(/\r?\n/).map((l) => l.split("\t").map((c) => c.trim())).filter((r) => r.some(Boolean));
    if (!lines.length) return [];
    const head = lines[0].map((h) => h.toLowerCase());
    const hasHead = head.some((h) => h.includes("associate") || h.includes("first"));
    const col = (names: string[], fallback: number) => { const i = head.findIndex((h) => names.some((n) => h.includes(n))); return hasHead && i >= 0 ? i : fallback; };
    const c = { first: col(["first"], 0), last: col(["last"], 1), id: col(["associate", "agent code", "id"], 2), md: col(["approval", "md"], 3), status: col(["status 1", "status"], 4), level: col(["level"], 6) };
    const toDate = (s: string) => { if (!s) return null; const d = dayjs(s); if (d.isValid()) return d.format("YYYY-MM-DD"); const n = Number(s); return n > 20000 ? dayjs("1899-12-30").add(n, "day").format("YYYY-MM-DD") : null; };
    return (hasHead ? lines.slice(1) : lines).filter((r) => r[c.id] && !/associate/i.test(r[c.id]))
      .map((r) => ({ first_name: r[c.first], last_name: r[c.last], associate_id: r[c.id], md_approval_date: toDate(r[c.md]), status: r[c.status] || "Active", level: r[c.level] }));
  };
  const parseLicensed = (txt: string) => txt.split(/\r?\n/).flatMap((l) => l.split("\t")).map((s) => s.trim()).filter((s) => /^[A-Z0-9]{4,8}$/i.test(s) && !/^(first|last|name)$/i.test(s));
  const a = parseActive(active), l = parseLicensed(licensed);

  async function apply() {
    setBusy(true); setErr(null);
    try {
      const res = await db.rpc("apply_agent_export", { p_active: a, p_licensed: l });
      setResult(res); invalidate("dash:team"); invalidate("team:"); toast("Export loaded");
    } catch (e) { setErr(e as Error); } finally { setBusy(false); }
  }
  return (
    <Sheet title="Update from the platform export" onClose={onClose}
      footer={result ? <button className="btn primary" onClick={onClose}>Done</button> : <>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={busy || a.length === 0} onClick={apply}>{busy ? "Loading…" : `Load ${a.length} agents`}</button>
      </>}>
      <ErrorBox error={err} />
      {result ? (
        <div className="stack">
          <p>Loaded <b>{result.rows}</b> agents from the export ({result.licensed} licensed). <b>{result.matched}</b> matched your team; level, licensed and MD approval date are updated.</p>
          <Panel title={`On your team, not in the export (${result.not_in_export.length})`} hint="Check if they were terminated">
            <div className="panel-body small">{result.not_in_export.length ? result.not_in_export.map((x: any) => `${x.name} (${x.agent_code})`).join(" · ") : "None"}</div>
          </Panel>
          <Panel title={`In the export, not on your team (${result.missing_from_team.length})`} hint="Add them to the team">
            <div className="panel-body small">{result.missing_from_team.length ? result.missing_from_team.map((x: any) => `${x.name} (${x.agent_code})`).join(" · ") : "None"}</div>
          </Panel>
        </div>
      ) : (
        <>
          <p className="small muted" style={{ margin: 0 }}>Copy the rows from the platform (or your Active Agent sheet) and paste them below, headings included. Columns: First Name, Last Name, Associate ID, MD Approval Date, Status, Agent Level.</p>
          <label className="field"><span>Active agents · {a.length} recognised</span>
            <textarea className="textarea mono" rows={7} value={active} onChange={(e) => setActive(e.target.value)} placeholder={"First Name\tLast Name\tAssociate ID\tMD Approval Date\tStatus 1\tStatus 2\tAgent Level"} /></label>
          <label className="field"><span>Licensed agents (any rows that include the Associate ID) · {l.length} recognised</span>
            <textarea className="textarea mono" rows={5} value={licensed} onChange={(e) => setLicensed(e.target.value)} /></label>
        </>
      )}
    </Sheet>
  );
}
